import React from 'react';
import i18n from 'i18next';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import HelpIcon from '@mui/icons-material/Help';
import Link from '@mui/material/Link';

import { MODALS } from '~redux/Modal/constants';
import ModalActions from '~redux/Modal/actions';
import { SwitchAuto } from '~app/components/Toolbar/components/SwitchAuto';
import TimeModifier from '~app/components/Toolbar/components/TimeModifier';
import { TIME_MODIFIER_VARIANTS } from '~app/components/Toolbar/components/TimeModifier/constants';
import { isPrinterManagerFeatureEnabled } from '~constants/features';

import { Printer } from '../Printer';

import styles from './styles.module.scss';
import SidebarSection from './SidebarSection';

const SettingsSidebar = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.modal[`${MODALS.SETTINGS}ModalIsOpen`]);
  const { autoAcceptOrder, enabledManualDelivery, autoReassignDelivery } =
    useSelector((state) => state?.stores?.storeById) || {};
  const { storeById } = useSelector((state) => state.stores);

  const closeModal = () => dispatch(ModalActions.closeModal(MODALS.SETTINGS));

  return (
    <Drawer anchor="right" open={open}>
      <Container maxWidth="342px" sx={{ marginTop: '15px' }}>
        <div className={styles.settingsSidebarContainer}>
          <Stack direction="row" justifyContent="flex-end" className={styles.closeBtn}>
            <IconButton onClick={closeModal} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Typography variant="subtitle1" fontWeight="fontWeightBold" className={styles.title}>
            {i18n.t('SettingsSidebar:config')}
          </Typography>
          <SidebarSection
            containerClassName={styles.autoAcceptOrders}
            title={i18n.t('SettingsSidebar:autoAcceptOrders')}>
            <SwitchAuto
              variant="autoAcceptOrder"
              customLabel={
                autoAcceptOrder ? i18n.t('SettingsSidebar:enabled') : i18n.t('SettingsSidebar:disabled')
              }
            />
          </SidebarSection>
          <SidebarSection
            containerClassName={styles.automaticDelivery}
            title={i18n.t('SettingsSidebar:automaticDelivery')}>
            <SwitchAuto
              variant="enabledManualDelivery"
              customLabel={
                enabledManualDelivery ? i18n.t('SettingsSidebar:enabled') : i18n.t('SettingsSidebar:disabled')
              }
            />
          </SidebarSection>
          <SidebarSection
            containerClassName={styles.deliveryReassignment}
            title={i18n.t('SettingsSidebar:deliveryReassignment')}>
            <SwitchAuto
              variant="autoReassignDelivery"
              customLabel={
                autoReassignDelivery ? i18n.t('SettingsSidebar:enabled') : i18n.t('SettingsSidebar:disabled')
              }
            />
          </SidebarSection>
          <SidebarSection
            containerClassName={styles.deliveryTimes}
            title={i18n.t('SettingsSidebar:deliveryTimes')}>
            <TimeModifier
              variant={TIME_MODIFIER_VARIANTS.rounded}
              deliveryWaitTime={storeById.deliveryWaitTime || 0}
              pickUpWaitTime={storeById.pickUpWaitTime || 0}
            />
          </SidebarSection>
          {isPrinterManagerFeatureEnabled ? (
            <SidebarSection
              containerClassName={styles.print}
              title={
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight="fontWeightBold" color="text.secondary">
                    {i18n.t('SettingsSidebar:print')}
                  </Typography>
                  <Stack direction="row" alignItems="center">
                    <HelpIcon color="info" fontSize="small" />
                    <Typography variant="caption">
                      <Link>{i18n.t('SettingsSidebar:printHelp')}</Link>
                    </Typography>
                  </Stack>
                </Stack>
              }>
              <Printer />
            </SidebarSection>
          ) : null}
        </div>
      </Container>
    </Drawer>
  );
};

export default SettingsSidebar;
